import { isNormalisedPhone, isValidDay } from '@wodi/domain';
import { z } from 'zod';

// Building blocks shared by the patient, visit and settings schemas.

/** A calendar date with no time or zone, `2026-09-22`: a date of birth, not an instant. */
export const isoDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be a date written as YYYY-MM-DD')
  .refine((text) => {
    const [year, month, day] = text.split('-').map(Number);
    return isValidDay({ year: year ?? 0, month: month ?? 0, day: day ?? 0 });
  }, 'That date does not exist');

/**
 * A Kenyan mobile number in its stored form, `2547…` or `2541…` (CLAUDE.md). The client
 * normalises what was typed with `normalisePhone` from @wodi/domain and shows its error
 * codes inline; the API accepts only the stored form.
 */
export const msisdnSchema = z
  .string()
  .refine(isNormalisedPhone, 'Must be a normalised Kenyan mobile number (2547… or 2541…)');

/**
 * A choice from a configured value set, as the client sends it. The server looks up the
 * display text in the tenant's value set; it never trusts display text from the client.
 */
export const codedRefSchema = z.object({
  system: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(64),
});

/** A stored coded value: code, display and code system, captured together (domain rule 9). */
export const codedValueSchema = z.object({
  system: z.string(),
  code: z.string(),
  display: z.string(),
});

export type CodedValue = z.infer<typeof codedValueSchema>;

/**
 * A coded value where the code list isn't settled yet (residence: TODO(verify-spec)).
 * Free text is allowed: the display alone, with no code or system.
 */
export const optionallyCodedValueSchema = z
  .object({
    system: z.string().trim().min(1).max(200).nullable(),
    code: z.string().trim().min(1).max(64).nullable(),
    display: z.string().trim().min(1).max(200),
  })
  .refine((v) => (v.code === null) === (v.system === null), {
    message: 'A code needs its code system, and a code system needs a code',
  });

/** An age as the banner and search results show it; see `ageAt` in @wodi/domain. */
export const ageSchema = z.object({
  unit: z.enum(['days', 'months', 'years']),
  value: z.number().int().min(0),
});

/** A reason for a correction, a cancellation or an override. Stored and audited. */
export const reasonSchema = z.string().trim().min(5).max(500);

export const enteredInErrorRequestSchema = z.object({ reason: reasonSchema });

/** The same "active or entered in error" status every correctable record carries. */
export const RECORD_STATUSES = ['active', 'entered_in_error'] as const;

/** Who did something and when, for history views. The name is a staff member's, not a patient's. */
export const actorSchema = z.object({ id: z.uuid(), displayName: z.string() });
